<template>
  <v-card
    class="mb-3"
    color="pwt_snow"
    @click="showTrip"
    :loading="fetching"
    :disabled="fetching"
  >
    <v-row no-gutters align="center">
      <v-card-title
        v-if="trip.name"
        class="text-truncate"
        style="word-break: normal; max-width: 280px"
      >
        <span class="text-truncate">{{ trip.name }}</span>
      </v-card-title>
      <v-card-title
        v-else
        class="text-truncate"
        style="word-break: normal; max-width: 280px"
      >
        <span class="text-truncate text-h6">
          Trip to
          {{
            trip.locations[trip.locations.length - 1]?.name ??
            trip.locations[trip.locations.length - 1]?.address ??
            "unknown"
          }}
        </span>
      </v-card-title>
    </v-row>
    <v-card-text>
      <ul style="list-style: none; padding-left: unset">
        <li>{{ niceDuration(trip.itinerary.totalTime) }}</li>
      </ul>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn icon @click.stop="saveTrip">
        <v-progress-circular
          indeterminate
          color="primary"
          size="30"
          v-if="processing && savingStatus === 'PROCESSING'"
        ></v-progress-circular>
        <v-icon
          color="success"
          large
          v-else-if="processing && savingStatus === 'SUCCESS'"
        >
          mdi-check</v-icon
        >
        <v-icon
          color="error"
          large
          v-else-if="processing && savingStatus === 'FAILED'"
        >
          mdi-close</v-icon
        >
        <v-icon color="primary" large v-else> mdi-content-save</v-icon>
      </v-btn>
    </v-card-actions>
    <v-dialog v-model="showDialog" width="300">
      <v-card class="rounded-lg">
        <v-card-title> Name it and save it </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="inputtedName"
            label="name"
            clearable
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <CustomSolidButton @click="nameAndSave"> Save </CustomSolidButton>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup lang="ts">
import CustomSolidButton from "@/components/ui-elements/buttons/custom-buttons/CustomSolidButton.vue";
import { useFleetStore } from "@/domains/fleet/fleetStore";
import { useTripStore, type Trip } from "@/domains/trips";
import { useUserStore } from "@/domains/user/userStore";
import { getNiceDuration } from "@/utils/timeUtils";
import { computed, ref, watch } from "vue";

const props = defineProps<{ trip: Trip }>();

const showDialog = ref(false);
const inputtedName = ref("");
const processing = ref(false);
const savingStatus = ref<"PROCESSING" | "SUCCESS" | "FAILED" | null>(null);

const tripStore = useTripStore();
const fleetStore = useFleetStore();
const userStore = useUserStore();
const fetching = computed(() => tripStore.isFetching);

function niceDuration(seconds: number): string {
  return getNiceDuration(seconds);
}

function showTrip() {
  tripStore.showTrip(props.trip.id);
}

async function saveTrip(forceSave = false) {
  processing.value = true;
  // check if trip has name
  if (!props.trip.name && !forceSave) {
    showDialog.value = true;
    return;
  }

  const fleetId = fleetStore.fleetId;
  const currentUserId = userStore.getCurrentUser?.id;

  const outcome =
    fleetId && currentUserId
      ? await props.trip.saveTrip({ fleetId, currentUserId })
      : { success: false, error: "Unable to identify the current user or fleet" };
  if (outcome.success) {
    savingStatus.value = "SUCCESS";
  } else {
    savingStatus.value = "FAILED";
  }

  setTimeout(() => {
    processing.value = false;
    savingStatus.value = null;
  }, 1500);
}

function nameAndSave() {
  processing.value = true;
  if (inputtedName.value) {
    props.trip.name = inputtedName.value;
    saveTrip();
  } else {
    saveTrip(true);
  }
  showDialog.value = false;
}

watch(savingStatus, () => {
  if (!savingStatus.value) {
    processing.value = false;
  }
});
</script>
